import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Image from './image';
import { mkQueue } from './helpers';

export default function mkLazyImageComponent() {
  const animationQueue = mkQueue();
  const downloadQueue = mkQueue({
    onBatch(components) {
      animationQueue.enqueue(...components.map(component => component.onLoaded));
    },
  });

  let itemsPerRow;
  const imagesLoadedForProducts = new Set();

  const observer = new IntersectionObserver(
    entries => {
      const intersectingImages = [];
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          intersectingImages.push(entry.target.component);
        }
      });
      downloadQueue.enqueue(...intersectingImages.map(component => component.onEnqueued));
    },
    {
      rootMargin: '450px 0px',
    }
  );

  class LazyImageComponent extends PureComponent {
    constructor({ uniqueId }) {
      super();
      const loaded = imagesLoadedForProducts.has(`${uniqueId}_${itemsPerRow}`);
      this.state = {
        loaded,
        deferred: !loaded,
        hidden: !loaded,
      };

      this.onEnqueued = this.onEnqueued.bind(this);
      this.onLoaded = this.onLoaded.bind(this);
      this.onPictureRef = this.onPictureRef.bind(this);
    }

    componentWillUnmount() {
      if (this.rejectOngoingPromise) {
        this.rejectOngoingPromise('Too late. Unmounting.');
      }
    }

    onEnqueued() {
      const { loaded } = this.state;
      if (loaded) return Promise.resolve(this);

      const $img = this.pictureRef.querySelector('img');
      return new Promise((resolve, reject) => {
        this.rejectOngoingPromise = reject;

        $img.addEventListener(
          'load',
          () => {
            this.rejectOngoingPromise = null;
            resolve(this);
          },
          {
            once: true,
          }
        );

        this.setState({ deferred: false });
      });
    }

    onLoaded() {
      const { uniqueId } = this.props;
      imagesLoadedForProducts.add(`${uniqueId}_${itemsPerRow}`);
      this.setState({ hidden: false });
    }

    onPictureRef(pictureRef) {
      if (pictureRef === null) {
        observer.unobserve(this.pictureRef);
      } else {
        this.pictureRef = pictureRef;
        this.pictureRef.component = this;
        const { loaded } = this.state;
        if (!loaded) {
          observer.observe(this.pictureRef);
        }
      }
    }

    render() {
      const { className, mediaUrl, sizeS, sizeL, alt, sizeM } = this.props;
      const { deferred, hidden, loaded } = this.state;

      const media = {
        url: deferred ? '' : mediaUrl,
      };

      let classNameOverride = className;

      if (loaded) {
        classNameOverride += ' product-image--loaded';
      }

      if (hidden) {
        classNameOverride += ' product-image--hidden';
      }

      return (
        <Image
          alt={alt}
          className={classNameOverride}
          media={media}
          onPictureRef={this.onPictureRef}
          sizes={{ s: sizeS, m: sizeM, l: sizeL }}
        />
      );
    }
  }

  LazyImageComponent.propTypes = {
    uniqueId: PropTypes.string.isRequired,
    className: PropTypes.string.isRequired,
    mediaUrl: PropTypes.string.isRequired,
    sizeS: PropTypes.string.isRequired,
    sizeM: PropTypes.string.isRequired,
    sizeL: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  };

  return {
    Component: LazyImageComponent,

    setItemsPerRow(newItemsPerRow) {
      if (itemsPerRow === newItemsPerRow) return;

      itemsPerRow = newItemsPerRow;

      downloadQueue.setBatchSizeAndRestart(itemsPerRow);
      animationQueue.setBatchSizeAndRestart(itemsPerRow);
    },
  };
}
